import { zodResolver } from '@hookform/resolvers/zod'
import React from 'react'
import { useForm } from 'react-hook-form'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import { z } from 'zod'

import { Button } from '../../../shadcn/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../shadcn/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../shadcn/components/ui/form'
import { Input } from '../../../shadcn/components/ui/input'
import { Separator } from '../../../shadcn/components/ui/separator'
import { cn } from '../../../shadcn/utils'

const createFormSchema = (intl: IntlShape) =>
  z.object({
    email: z.string().email({
      message: intl.formatMessage({
        id: 'ui.ClientRequestVisitorModal.email.invalid',
        defaultMessage: 'Veuillez entrer un email valide',
      }),
    }),
  })

export type ClientRequestVisitorModalFormValues = z.infer<
  ReturnType<typeof createFormSchema>
>

type ClientRequestVisitorModalProps = {
  briefTitle: string
  contact?: {
    firstname: string
    lastname: string
  }
  onSubmit: (data: ClientRequestVisitorModalFormValues) => void
  onOpenLoginModal: (email: string) => void
  isOpen: boolean
  isLoading: boolean
}

export const ClientRequestVisitorModal = ({
  briefTitle,
  contact,
  onSubmit,
  onOpenLoginModal,
  isOpen,
  isLoading,
}: ClientRequestVisitorModalProps) => {
  const intl = useIntl()

  const form = useForm<ClientRequestVisitorModalFormValues>({
    resolver: zodResolver(createFormSchema(intl)),
    defaultValues: {
      email: '',
    },
  })

  return (
    <Dialog open={isOpen}>
      <DialogContent backdropStyle='dark' className='p-0'>
        <div className='flex flex-col'>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <DialogHeader className='flex items-start justify-center px-6 py-5'>
                <DialogTitle className='text-base font-medium'>
                  <FormattedMessage
                    id='ui.ClientRequestVisitorModal.title'
                    defaultMessage='Accéder au projet partagé'
                  />
                </DialogTitle>
              </DialogHeader>
              <Separator />
              <div className='flex flex-col gap-6 p-6'>
                <div className='text-sm'>
                  <FormattedMessage
                    id='ui.ClientRequestVisitorModal.description'
                    defaultMessage='Pour rejoindre le projet<b>{briefTitle}</b>, indiquez votre mail professionnel ou connectez-vous si vous êtes<b>{contactKey, select, hasContact {{contactName}} other {un collaborateur}}</b>.'
                    values={{
                      b: (chunks) => <b>&nbsp;{chunks}</b>,
                      briefTitle,
                      contactKey: contact ? 'hasContact' : 'other',
                      contactName:
                        contact && `${contact.firstname} ${contact.lastname}`,
                    }}
                  />
                </div>
                <div>
                  <FormField
                    control={form.control}
                    name='email'
                    render={({ field, fieldState: { error } }) => (
                      <FormItem>
                        <FormLabel className='text-sm font-normal'>
                          <FormattedMessage
                            id='ui.ClientRequestVisitorModal.email.label'
                            defaultMessage='E-mail professionnel'
                          />
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder={intl.formatMessage({
                              id: 'ui.ClientRequestVisitorModal.email.placeholder',
                              defaultMessage: 'Entrer votre e-mail',
                            })}
                            inputClassName={cn('h-12', {
                              'border-red-500': !!error,
                            })}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div>
                  <button
                    type='button'
                    className='text-sm text-[blue]'
                    onClick={() => onOpenLoginModal(form.getValues('email'))}
                  >
                    <FormattedMessage
                      id='ui.ClientRequestVisitorModal.organizer'
                      defaultMessage="Vous êtes l'organisateur du projet ? Se connecter"
                    />
                  </button>
                </div>
              </div>
              <Separator />
              <DialogFooter className='flex items-end p-6'>
                <Button type='submit' className='w-1/2' isLoading={isLoading}>
                  <FormattedMessage
                    id='ui.ClientRequestVisitorModal.confirm'
                    defaultMessage='Confirmer'
                  />
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  )
}
